import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomButton from "shared/components/common/customButton";
import { toastMessage } from "shared/components/common/toast";
import CustomInput from "shared/components/common/customInput";
import CustomTextEditor from "shared/components/common/customTextEditor";
import {
  CreatePolicyService,
  UpdatePolicyService,
  GetPolicy,
} from "shared/services/policy";

const Policy = ({ type }: { type: string }) => {
  const [policyId, setPolicyId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [noData, setNoData] = useState<boolean>(false);

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    detail: Yup.string().required("Detail is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      detail: "",
      type: type,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        let payload: any = {
          title: values.title,
          detail: values.detail,
        };

        if (!policyId) {
          payload["type"] = values.type;
        }

        if (policyId) {
          await UpdatePolicyService(policyId, payload);
          toastMessage("success", "Policy updated successfully!");
        } else {
          await CreatePolicyService(payload);
          toastMessage("success", "Policy created successfully!");
        }

        fetchPolicy();
      } catch (error) {
        console.error(
          `Failed to ${policyId ? "update" : "create"} policy:`,
          error
        );
        toastMessage(
          "error",
          `Failed to ${policyId ? "update" : "create"} policy.`
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await GetPolicy(type);

      if (response?.data) {
        const { id, title, detail } = response.data.data;
        setPolicyId(id);

        formik.setValues({
          title: title || "",
          detail: detail || "",
          type,
        });

        setNoData(false);
      } else {
        setNoData(true);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPolicyId(null);
    fetchPolicy();
  }, [type]);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1 className="font-medium">{policyId ? `${type}` : `Create ${type}`}</h1>

      {noData ? (
        <div className="text-center text-gray-500">
          <p>No existing {type} data found. Please create a new one.</p>
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-4">
            <CustomInput
              required
              label="Title"
              placeholder="Enter Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.title && formik.errors.title
                  ? formik.errors.title
                  : ""
              }
            />
          </div>

          <div className="my-4">
            <CustomTextEditor
              value={formik.values.detail}
              onChange={(value) => {
                formik.setFieldValue("detail", value);
              }}
              placeholder="Enter detail here"
            />
          </div>

          <div className="flex justify-end">
            <CustomButton
              title="Create/ Update Policy"
              loading={loading}
              type="submit"
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default Policy;
